import { ReactNode } from 'react';
import { useAuth } from '@/contexts/AuthContext';

// ─────────────────────────────────────────────
// HOOK useUser
// ─────────────────────────────────────────────

/**
 * Hook pour accéder à l'utilisateur courant et ses rôles.
 * Wrapper de useAuth() pour compatibilité avec le code existant.
 *
 * Utilisation recommandée : useAuth() directement pour le nouveau code.
 * Ce hook reste utile pour les composants qui n'ont besoin que de
 * user + role sans le reste du contexte auth.
 */
export function useUser() {
  const { user, loading } = useAuth();

  // Rôle principal (premier rôle du tableau)
  const role = user?.roles && user.roles.length > 0 ? user.roles[0] : null;

  // Helper : vérifie si l'utilisateur a un rôle spécifique
  const hasRole = (r: string): boolean =>
    user?.roles?.includes(r) ?? false;

  // Helper : vérifie si l'utilisateur a au moins un des rôles listés
  const hasAnyRole = (roles: string[]): boolean =>
    user?.roles?.some(r => roles.includes(r)) ?? false;

  return {
    user: user
      ? {
          id: user.id,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL ?? null,
          emailVerified: user.emailVerified,
          roles: user.roles ?? [],
        }
      : null,
    role,
    loading,
    hasRole,
    hasAnyRole,
  };
}

// ─────────────────────────────────────────────
// COMPOSANT RequireRole
// ─────────────────────────────────────────────

type RequireRoleProps = {
  allowed: string[];
  fallback?: ReactNode;
  children: ReactNode;
};

/**
 * Restreint l'accès selon les rôles Firebase de l'utilisateur.
 * Affiche un spinner pendant le chargement.
 * Affiche fallback (ou message par défaut) si accès refusé.
 */
export function RequireRole({ allowed, fallback, children }: RequireRoleProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const hasAccess = user?.roles?.some((r: string) => allowed.includes(r)) ?? false;

  if (!hasAccess) {
    return (
      <>
        {fallback || (
          <div className="p-6 text-center text-muted-foreground">
            Access denied. Required role{allowed.length > 1 ? 's' : ''}: {allowed.join(', ')}
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}